import type { CSSProperties } from 'react';
import React, { useMemo } from 'react';
import { ArrowDownward as ArrowDownwardIcon } from '@mui/icons-material';
import { HiddenContentChipProps } from './HiddenContentChip.props';
import {
  BottomChip,
  DivBottomBorder,
  DivLeftBorder,
  DivRightBorder,
  DivTopBorder,
  LeftChip,
  RightChip,
  Root,
  TopChip,
} from './HiddenContentChip.styles';

const HiddenContentChip = ({ id = '', isVisible = false, label = 'Read More', position }: HiddenContentChipProps) => {
  const borderComponent = useMemo(() => {
    switch (position) {
      case 'top':
        return DivBottomBorder;
      case 'right':
        return DivLeftBorder;
      case 'bottom':
        return DivTopBorder;
      case 'left':
        return DivRightBorder;
      default:
        return DivBottomBorder;
    }
  }, [position]);

  const ChipComponent = useMemo(() => {
    switch (position) {
      case 'top':
        return TopChip;
      case 'right':
        return RightChip;
      case 'bottom':
        return BottomChip;
      case 'left':
        return LeftChip;
      default:
        return BottomChip;
    }
  }, [position]);

  const rootStyle = useMemo<CSSProperties>(() => {
    switch (position) {
      case 'top':
        return { bottom: 0 };
      case 'right':
        return { justifyContent: 'flex-start', left: 0, right: 'auto', top: '50%', transform: 'translateY(-50%)' };
      case 'bottom':
        return { top: 0 };
      case 'left':
        return { justifyContent: 'flex-end', left: 'auto', right: 0, top: '50%', transform: 'translateY(-50%)' };
      default:
        return { top: 0 };
    }
  }, [position]);

  const Border = borderComponent;
  const ChipStyled = ChipComponent;

  return (
    <Border id={id} data-testid={id}>
      <Root aria-hidden={!isVisible} style={rootStyle}>
        <ChipStyled
          color="primary"
          icon={<ArrowDownwardIcon />}
          label={label}
          size="small"
          style={isVisible ? undefined : { opacity: 0, visibility: 'hidden' }}
        />
      </Root>
    </Border>
  );
};

export default HiddenContentChip;
